import React, { useEffect, useState } from "react";
import { motion, Variants } from "framer-motion";
import { CheckCircle, Star } from "lucide-react";
import {confirmKarmaPayment, confirmPayment} from "../api/api";
import { useAuth } from "../context/AuthContext";

const confettiCount = 30;

const Confetti: React.FC = () => {
    const pieces = Array.from({ length: confettiCount });
    return (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {pieces.map((_, i) => {
                const size = Math.random() * 8 + 4;
                const left = Math.random() * 100;
                const delay = Math.random() * 0.5;
                const duration = Math.random() * 2 + 2;
                const hue = Math.random() * 360;
                return (
                    <motion.div
                        key={i}
                        className="rounded-sm"
                        style={{
                            width: size,
                            height: size,
                            backgroundColor: `hsl(${hue}, 98%, 68%)`,
                            left: `${left}%`,
                            top: "-10%",
                            position: "absolute",
                        }}
                        animate={{
                            y: ["-10%", "110%"],
                            rotate: [0, 360],
                        }}
                        transition={{
                            delay,
                            duration,
                            ease: "easeOut",
                            repeat: Infinity,
                            repeatType: "loop",
                        }}
                    />
                );
            })}
        </div>
    );
};

const textVariants: Variants = {
    hidden: { y: 20, opacity: 0 },
    visible: (i: number) => ({
        y: 0,
        opacity: 1,
        transition: { delay: 0.45 + i * 0.18, duration: 0.7, ease: "easeOut" },
    }),
};

const Success: React.FC = () => {
    const { isLoading, isAuthenticated } = useAuth();
    const [isKarma, setIsKarma] = useState(false);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const topic = params.get("topic");
        console.log("topic", topic);
        if (topic === "karma") {
            setIsKarma(true);
            const session_id = params.get("session_id");
            if (session_id && isAuthenticated) {
                confirmKarmaPayment(session_id).then((result) => {
                    if (result.error) {
                        // Handle error (show toast, redirect, etc.)
                        window.location.href = "/failed";
                    } else {
                        // Optionally set more state for upgraded karmaPower, etc.
                    }
                });
            }
            return;
        }

        // Booking payment confirmation
        const payment_session_id = params.get("session_id");
        if (!payment_session_id) {
            // window.location.href = "/failed";
            return;
        }
        if (isLoading) return;
        if (isAuthenticated) {
            confirmPayment(payment_session_id).catch((error) => {
                console.error("Payment confirmation failed:", error);
                window.location.href = "/failed";
            });
        }
    }, [isLoading, isAuthenticated]);

    return (
        <div
            className="relative flex flex-col items-center justify-center w-screen h-screen bg-gradient-to-br from-white via-gray-100 to-gray-200 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900"
        >
            <Confetti />
            {/* Confirmation Card */}
            <div className="relative flex flex-col items-center justify-center rounded-2xl bg-white/90 dark:bg-gray-900/90 border border-primary/20 shadow-2xl px-8 py-12 max-w-lg w-full z-10">
                {isKarma ? (
                    <>
                        <motion.div
                            className="mb-5"
                            initial={{ scale: 0.85, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.65, type: "spring", bounce: 0.35 }}
                        >
                            <Star className="w-24 h-24 text-yellow-400 drop-shadow animate-bounce" strokeWidth={1.5} />
                        </motion.div>

                        <motion.h1
                            className="text-3xl md:text-4xl font-extrabold text-yellow-800 dark:text-yellow-100 mb-2 tracking-tight"
                            custom={0}
                            initial="hidden"
                            animate="visible"
                            variants={textVariants}
                        >
                            Karma Score Upgraded!
                        </motion.h1>

                        <motion.p
                            className="text-gray-700 mb-5 text-center max-w-md font-medium dark:text-gray-200"
                            custom={1}
                            initial="hidden"
                            animate="visible"
                            variants={textVariants}
                        >
                            🎉 Your restaurant’s <b>Karma Score</b> has been successfully upgraded!
                            <br />
                            <span className="text-yellow-700 font-semibold block mt-2 dark:text-yellow-300">
                                Your visibility and chances of being picked by guests just got a major boost!
                            </span>
                        </motion.p>

                        <motion.div
                            className="mb-8 text-gray-600 text-center max-w-lg text-sm leading-relaxed dark:text-gray-300"
                            custom={2}
                            initial="hidden"
                            animate="visible"
                            variants={textVariants}
                        >
                            <span className="block mb-1">
                                <b>Karma Score</b> increases your ranking and makes your restaurant more visible in user searches and recommendations.
                            </span>
                            <span className="block mb-1">
                                The higher your score, the more likely you are to attract bookings and grow your business.
                            </span>
                            <span className="block mt-2">
                                <span className="text-primary font-semibold">Thank you for investing in your restaurant’s success!</span>
                            </span>
                        </motion.div>

                        <motion.button
                            className="px-8 py-3 bg-primary text-white font-bold rounded-md shadow-lg hover:bg-primary/90 focus:outline-none text-lg"
                            custom={3}
                            initial="hidden"
                            animate="visible"
                            variants={textVariants}
                            whileTap={{ scale: 0.97 }}
                            onClick={() => (window.location.href = "/profile")}
                        >
                            View My Profile
                        </motion.button>
                    </>
                ) : (
                    <>
                        <motion.div
                            className="mb-5"
                            initial={{ scale: 0.85, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.65, type: "spring", bounce: 0.35 }}
                        >
                            <CheckCircle className="w-24 h-24 text-green-500 drop-shadow" strokeWidth={1.5} />
                        </motion.div>

                        <motion.h1
                            className="text-3xl md:text-4xl font-extrabold text-gray-800 mb-2 tracking-tight dark:text-gray-100"
                            custom={0}
                            initial="hidden"
                            animate="visible"
                            variants={textVariants}
                        >
                            Booking Confirmed!
                        </motion.h1>

                        <motion.p
                            className="text-gray-700 mb-5 text-center max-w-md font-medium dark:text-gray-200"
                            custom={1}
                            initial="hidden"
                            animate="visible"
                            variants={textVariants}
                        >
                            🎉 Your table is all set! We're already working with our partner restaurants to lock in your surprise experience.
                            <br />
                            <span className="text-green-700 font-semibold block mt-2 dark:text-green-400">
                                Most of our partners confirm instantly.<br />
                                If not, you'll get an update as soon as they accept your booking.
                            </span>
                        </motion.p>

                        <motion.div
                            className="mb-8 text-gray-600 text-center max-w-lg text-sm leading-relaxed dark:text-gray-300"
                            custom={2}
                            initial="hidden"
                            animate="visible"
                            variants={textVariants}
                        >
                            <span className="block mb-1">
                                <b>How it works:</b> Some restaurants confirm automatically, while others review bookings before accepting.
                            </span>
                            <span className="block mb-1">
                                If your surprise restaurant’s final price is lower than what you paid, <span className="text-green-600 font-bold dark:text-green-400">you’ll be refunded the difference</span>—automatically, with no action needed.
                            </span>
                            <span className="block mb-1">
                                <span className="text-primary font-semibold">Did you know?</span> In over <b>70%</b> of cases, our guests receive a refund after their visit!
                            </span>
                            <span className="block mt-2">
                                Thank you for trusting SurpriseTable. We’re committed to making your experience special and completely risk-free.
                            </span>
                        </motion.div>

                        <motion.button
                            className="px-8 py-3 bg-primary text-white font-bold rounded-md shadow-lg hover:bg-primary/90 focus:outline-none text-lg"
                            custom={3}
                            initial="hidden"
                            animate="visible"
                            variants={textVariants}
                            whileTap={{ scale: 0.97 }}
                            onClick={() => (window.location.href = "/profile")}
                        >
                            View My Bookings
                        </motion.button>
                    </>
                )}
            </div>
        </div>
    );
};

export default Success;
